import { useState, useEffect, useCallback, useMemo } from 'react';

export interface TableFilterState {
    search: string;
    sortBy: string;
    [key: string]: any; // Allow custom filters per table
}

/**
 * Hook for managing table filters with localStorage persistence
 * Each table has its own unique storage key to maintain separate filter states
 */
export const useTableFilters = (tableId: string, defaultFilters: TableFilterState) => {
    const storageKey = `table-filters-${tableId}`;
    const [filters, setFilters] = useState<TableFilterState>(defaultFilters);
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

    // Load filters from localStorage on mount
    useEffect(() => {
        try {
            const stored = localStorage.getItem(storageKey);
            if (stored) {
                const parsedFilters = JSON.parse(stored);
                setFilters(parsedFilters);
            }
        } catch (error) {
            console.error(`Failed to load filters for table ${tableId}:`, error);
        }
    }, [storageKey]);

    // Save filters to localStorage whenever they change
    useEffect(() => {
        try {
            localStorage.setItem(storageKey, JSON.stringify(filters));
        } catch (error) {
            console.error(`Failed to save filters for table ${tableId}:`, error);
        }
    }, [filters, storageKey]);

    // Update a single filter
    const updateFilter = useCallback((key: string, value: any) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    }, []);

    // Update multiple filters at once
    const updateFilters = useCallback((newFilters: Partial<TableFilterState>) => {
        setFilters(prev => ({ ...prev, ...newFilters }));
    }, []);

    // Clear all filters back to defaults
    const clearFilters = useCallback(() => {
        setFilters(defaultFilters);
    }, [defaultFilters]);

    // Check if any non-sort, non-search filters are active
    const hasActiveFilters = useMemo(() => {
        for (const [key, value] of Object.entries(filters)) {
            if (key === 'search' || key === 'sortBy') continue;
            if (value === '' || value === null || value === undefined || value === false) continue;
            return true;
        }
        return false;
    }, [filters]);

    return {
        filters,
        updateFilter,
        updateFilters,
        clearFilters,
        isFilterModalOpen,
        setIsFilterModalOpen,
        hasActiveFilters,
    };
};
